import { OrbitControls, useGLTF } from '@react-three/drei'
import { DRACO_DECODER_PATH, isV2ModelPath } from 'features/Procedure/hooks/useEvaluateModel'
import { useFrame, useThree, type ThreeEvent } from '@react-three/fiber'
import { useAppSelector } from 'common/hooks/redux'
import { useDrawingElementId } from 'features/Procedure/hooks/useDrawingElementId'
import { useSceneActions } from 'features/Procedure/hooks/useSceneActions'
import {
  beginPointerSession,
  consumeEraseHit,
  createPointerSession,
  isActivePointerSession,
  isElementErasable,
  isTapGesture,
  resetPointerSession,
} from 'features/Procedure/procedure-model/body/eraser.utils'
import { FreeLine, LineDot, StraightLine } from 'features/Procedure/procedure-model/lines'
import { materialCache } from 'features/Procedure/procedure-model/lines/material-cache'
import { BVH_SPLIT_STRATEGY } from 'features/Procedure/procedure-model/lib/bvh-setup'
import { Pin } from 'features/Procedure/procedure-model/pin'
import { XPin } from 'features/Procedure/procedure-model/x-pin'
import { ProcedureInitialState } from 'features/Procedure/state/procedure.slice'
import {
  DrawLineState,
  DrawMode,
  DrawingElementVisibleState,
  type DrawingElement,
} from 'features/Procedure/state/procedure.type'
import { normalizeStrokeWidthToFreeLinePreset } from 'features/Procedure/utils/drawingElementFactory'
import {
  distanceSq,
  getMinCaptureDistanceSq,
  simplifyFreeLinePoints,
} from 'features/Procedure/utils/freeLineSimplification'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { Group } from 'three'
import * as THREE from 'three'
import { Vector3, type Vector3Like } from 'three'
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib'
import { OrbitControlsSensitivity, V2_SCENE_OFFSET } from './body.constants'

type BodyProps = {
  debugMode?: boolean
  drawingElements: DrawingElement[]
  modelPath: string
  skinColor?: string
  previewColor?: string
  drawColor: string
  drawWidth: number
  cameraPosition: [number, number, number]
  cameraTarget: [number, number, number]
  canvasRef: React.RefObject<HTMLCanvasElement>
  disableDrawing?: boolean
  isNotesMode?: boolean
  readOnly?: boolean
  inactiveProcedureOpacity?: number
}

export type BodyRef = {
  zoomToPin: (pinId: string) => void
  zoomToElement: (elementId: string) => Promise<void>
  resetCamera: () => void
}

type BodyEventHandlers = {
  pointerDown: (event: PointerEvent) => void
  pointerUp: (event: PointerEvent) => void
  pointerMove: (event: PointerEvent) => void
  pointerCancel: (event: PointerEvent) => void
}

const noopPointerHandler = (() => {}) as BodyEventHandlers['pointerDown']
const createInitialHandlers = (): BodyEventHandlers => ({
  pointerDown: noopPointerHandler,
  pointerUp: noopPointerHandler,
  pointerMove: noopPointerHandler,
  pointerCancel: noopPointerHandler,
})

const Body = forwardRef<BodyRef, BodyProps>(
  (
    {
      debugMode = false,
      drawingElements,
      modelPath,
      skinColor,
      previewColor = '#00FF00',
      drawColor,
      drawWidth,
      cameraPosition,
      cameraTarget,
      canvasRef,
      disableDrawing = false,
      readOnly = false,
      inactiveProcedureOpacity = 0,
    },
    ref,
  ) => {
    const groupRef = useRef<Group>(null)
    const previousModelPathRef = useRef<string | null>(null)
    const suppressCanvasDeselectRef = useRef(false)
    const rafRef = useRef<number | null>(null)
    const pointerSessionRef = useRef(createPointerSession())
    // Stable ref for latest event handlers - avoids tearing down and re-adding
    // all 13+ canvas listeners on every draw mode or state change.
    const handlersRef = useRef<BodyEventHandlers>(createInitialHandlers())
    const [controls, setControls] = useState<OrbitControlsImpl | null>(null)
    const gltf = useGLTF(modelPath, DRACO_DECODER_PATH)
    const scene = gltf.scene
    const { camera, raycaster, pointer, invalidate, gl, setSize } = useThree()

    useEffect(() => {
      if (!scene) return
      scene.traverse((obj) => {
        const mesh = obj as THREE.Mesh
        if (!mesh.isMesh) return
        const geom = mesh.geometry as THREE.BufferGeometry | undefined
        if (!geom || geom.boundsTree) return
        geom.computeBoundsTree?.({ strategy: BVH_SPLIT_STRATEGY, maxLeafTris: 10 })
      })
    }, [scene])

    const drawMode = useAppSelector((state) => state.procedureReducer.drawMode)
    const drawLineState = useAppSelector((state) => state.procedureReducer.drawLineState)
    const desiredCameraPosition = useAppSelector(
      (state) => state.procedureReducer.desiredCameraPosition,
    )
    const selectedElement = useAppSelector((state) => state.procedureReducer.selectedElement)
    const selectedProcedure = useAppSelector((state) => state.procedureReducer.selectedProcedure)
    const {
      handleSetDrawLineState,
      handleSetDesiredCameraPosition,
      handleAddDrawingElement,
      handleSetSelectedElement,
      handleDeleteDrawingElement,
      handleUpdateSceneCoords,
    } = useSceneActions()
    const { generateDrawingElementId } = useDrawingElementId()

    const [isSceneReady, setIsSceneReady] = useState(false)
    // Free-line drawing state kept in refs to avoid re-rendering Body on every mousemove.
    // The preview mesh is updated imperatively via useFrame instead.
    const currentLineRef = useRef<Vector3[]>([])
    const isDrawingRef = useRef(false)
    const previewMeshRef = useRef<THREE.Mesh>(null)
    const previewDirtyRef = useRef(false)
    const drawRafPendingRef = useRef(false)
    const pendingDrawCoordsRef = useRef<{ x: number; y: number } | null>(null)
    const [straightLineStartPoint, setStraightLineStartPoint] = useState<Vector3 | null>(null)
    const [straightLineEndPoint, setStraightLineEndPoint] = useState<Vector3 | null>(null)
    const [previewEndPoint, setPreviewEndPoint] = useState<Vector3 | null>(null)
    const pendingMeasureLineRef = useRef<{ id: string; tooltipPosition: Vector3Like } | null>(null)

    const isDrawingLine = !disableDrawing && drawMode === DrawMode.FREE_LINE
    const isDrawingStraightLine = !disableDrawing && drawMode === DrawMode.STRAIGHT_LINE
    const isDrawingMeasure = !disableDrawing && drawMode === DrawMode.MEASURE
    const isAddingX = !disableDrawing && drawMode === DrawMode.X
    const isAddingPin = !disableDrawing && drawMode === DrawMode.PIN
    const isErasing = !disableDrawing && drawMode === DrawMode.ERASER

    // Only mount renderable elements. HIDDEN / DELETED / undo-stashed elements
    // still live in `drawingElements` for selection + undo lookups, but mounting
    // them inflates the scene tree (more geometries, materials, raycaster targets)
    // and drives memory pressure on patients with long histories.
    const renderableElements = useMemo(
      () =>
        drawingElements.filter(
          (el) =>
            el.visibleState === DrawingElementVisibleState.VISIBLE ||
            el.visibleState === DrawingElementVisibleState.PERSISTED,
        ),
      [drawingElements],
    )

    const useDepthTest = true

    const orbitControlsEnabled = disableDrawing || !drawMode

    const persistSceneCoords = useCallback(
      (coords?: { position: Vector3Like; target: Vector3Like }) => {
        const position = coords?.position ?? camera.position
        const target = coords?.target ?? controls?.target

        if (!target) return

        handleUpdateSceneCoords({
          position: {
            x: position.x,
            y: position.y,
            z: position.z,
          },
          target: {
            x: target.x,
            y: target.y,
            z: target.z,
          },
        })
      },
      [camera, controls, handleUpdateSceneCoords],
    )

    const syncViewportRender = useCallback(() => {
      const container = gl.domElement.parentElement
      const rect = container?.getBoundingClientRect() ?? gl.domElement.getBoundingClientRect()

      if (rect.width > 0 && rect.height > 0) {
        setSize(rect.width, rect.height)
      }

      controls?.update()
      invalidate()
    }, [controls, gl.domElement, invalidate, setSize])

    const applyStoredCamera = useCallback(() => {
      if (!controls) return

      camera.position.set(cameraPosition[0], cameraPosition[1], cameraPosition[2])
      controls.target.set(cameraTarget[0], cameraTarget[1], cameraTarget[2])
      controls.update()
      syncViewportRender()
    }, [camera, cameraPosition, cameraTarget, controls, syncViewportRender])

    useLayoutEffect(() => {
      applyStoredCamera()
    }, [applyStoredCamera, modelPath])

    useLayoutEffect(() => {
      syncViewportRender()

      let secondFrameId = 0
      const firstFrameId = requestAnimationFrame(() => {
        syncViewportRender()
        secondFrameId = requestAnimationFrame(syncViewportRender)
      })
      const timeoutId = window.setTimeout(syncViewportRender, 150)

      return () => {
        cancelAnimationFrame(firstFrameId)
        if (secondFrameId) cancelAnimationFrame(secondFrameId)
        window.clearTimeout(timeoutId)
      }
    }, [disableDrawing, drawMode, modelPath, syncViewportRender])

    useEffect(() => {
      const container = gl.domElement.parentElement
      if (!container || typeof ResizeObserver === 'undefined') return

      const observer = new ResizeObserver(syncViewportRender)
      observer.observe(container)

      return () => observer.disconnect()
    }, [gl.domElement, syncViewportRender])

    const capturePointerOnCanvas = useCallback((event: PointerEvent) => {
      const canvas = event.target instanceof HTMLCanvasElement ? event.target : null
      if (!canvas?.setPointerCapture) return

      try {
        canvas.setPointerCapture(event.pointerId)
      } catch {
        // Ignore pointer capture failures from cancelled or inactive pointers.
      }
    }, [])

    const releasePointerOnCanvas = useCallback((event: PointerEvent) => {
      const canvas = event.target instanceof HTMLCanvasElement ? event.target : null
      if (!canvas?.releasePointerCapture || !canvas.hasPointerCapture?.(event.pointerId)) return

      try {
        canvas.releasePointerCapture(event.pointerId)
      } catch {
        // Ignore pointer release failures; session reset is still safe.
      }
    }, [])

    const resetActivePointerSession = useCallback(
      (event?: PointerEvent) => {
        if (event) {
          releasePointerOnCanvas(event)
        }
        resetPointerSession(pointerSessionRef.current)
      },
      [releasePointerOnCanvas],
    )

    useEffect(() => {
      if (!isDrawingStraightLine && !isDrawingMeasure) {
        setStraightLineStartPoint(null)
        setStraightLineEndPoint(null)
        setPreviewEndPoint(null)
        handleSetDrawLineState(DrawLineState.IDLE)
      }
    }, [isDrawingStraightLine, isDrawingMeasure, handleSetDrawLineState])

    useEffect(() => {
      if (!isDrawingLine) {
        currentLineRef.current = []
        isDrawingRef.current = false
        previewDirtyRef.current = true
        invalidate()
      }
    }, [isDrawingLine, invalidate])

    useEffect(() => {
      if (isSceneReady) {
        invalidate()
      }
    }, [drawingElements, isSceneReady, invalidate])

    useEffect(() => {
      if (pendingMeasureLineRef.current) {
        const { id, tooltipPosition } = pendingMeasureLineRef.current
        const element = drawingElements.find((el) => el.id === id)
        if (element) {
          handleSetSelectedElement(element, tooltipPosition)
          pendingMeasureLineRef.current = null
        }
      }
    }, [drawingElements, handleSetSelectedElement])

    useEffect(() => {
      if (!desiredCameraPosition || !controls) return
      if (debugMode) {
        console.log('desiredCameraPosition', desiredCameraPosition)
      }
      const { position, target, animate = true, duration = 1000 } = desiredCameraPosition

      const endPos = new THREE.Vector3(position.x, position.y, position.z)
      const endTarget = new THREE.Vector3(target.x, target.y, target.z)

      if (rafRef.current) cancelAnimationFrame(rafRef.current)

      if (!animate) {
        camera.position.copy(endPos)
        controls.target.copy(endTarget)
        controls.update()
        invalidate()
        persistSceneCoords()
        handleSetDesiredCameraPosition(null)
        return
      }

      const startPos = camera.position.clone()
      const startTarget = controls.target.clone()
      let startTime: number | null = null

      const loop = (t: number) => {
        if (startTime === null) startTime = t
        const progress = Math.min((t - startTime) / duration, 1)

        camera.position.lerpVectors(startPos, endPos, progress)
        controls.target.lerpVectors(startTarget, endTarget, progress)
        controls.update()
        invalidate()

        if (progress < 1) {
          rafRef.current = requestAnimationFrame(loop)
        } else {
          rafRef.current = null
          persistSceneCoords()
          handleSetDesiredCameraPosition(null)
        }
      }

      rafRef.current = requestAnimationFrame(loop)

      return () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current)
        rafRef.current = null
        persistSceneCoords({ position: endPos, target: endTarget })
      }
    }, [
      desiredCameraPosition,
      camera,
      controls,
      debugMode,
      handleSetDesiredCameraPosition,
      invalidate,
      persistSceneCoords,
    ])

    const getSurfaceIntersection = useCallback(
      (clientX: number, clientY: number, strictMode = false) => {
        if (!scene) return null

        const canvas = canvasRef.current
        if (!canvas) return null

        const rect = canvas.getBoundingClientRect()
        pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1
        pointer.y = -((clientY - rect.top) / rect.height) * 2 + 1

        raycaster.setFromCamera(pointer, camera)

        // Single recursive raycast — intersectObject with recursive=true already
        // traverses all children and meshes, making the previous fallback passes redundant.
        const intersects = raycaster.intersectObject(scene, true)

        if (intersects.length > 0) {
          const { point, face } = intersects[0]
          // Small offset along surface normal so line sits on body; polygon offset avoids z-fighting
          const surfaceOffset = 0.002
          const offsetPoint = point
            .clone()
            .add(face?.normal?.clone().multiplyScalar(surfaceOffset) || new THREE.Vector3())
          return offsetPoint
        }

        if (strictMode) {
          return null
        }

        const bodyBox = new THREE.Box3().setFromObject(scene)
        const bodyCenter = bodyBox.getCenter(new THREE.Vector3())

        const cameraDirection = new THREE.Vector3()
        camera.getWorldDirection(cameraDirection)

        const plane = new THREE.Plane()
        plane.setFromNormalAndCoplanarPoint(cameraDirection, bodyCenter)

        const planeIntersection = new THREE.Vector3()
        const ray = raycaster.ray
        ray.intersectPlane(plane, planeIntersection)

        if (planeIntersection) {
          if (debugMode) {
            console.log('Projected onto camera-facing plane:', planeIntersection)
          }
          return planeIntersection
        }

        return null
      },
      [scene, pointer, raycaster, camera],
    )

    // Imperative preview update — runs once per frame, no React re-renders.
    useFrame(() => {
      if (!previewDirtyRef.current || !previewMeshRef.current) return
      previewDirtyRef.current = false

      const points = currentLineRef.current
      if (points.length < 2) {
        previewMeshRef.current.visible = false
        return
      }

      previewMeshRef.current.visible = true
      if (previewMeshRef.current.geometry) {
        previewMeshRef.current.geometry.dispose()
      }

      const curve = new THREE.CatmullRomCurve3(points, false)
      const segments = Math.min(points.length * 2, 64)
      previewMeshRef.current.geometry = new THREE.TubeGeometry(
        curve,
        segments,
        drawWidth / 100,
        6,
        false,
      )

      previewMeshRef.current.material = materialCache.getLineMaterial(
        previewColor,
        false,
        false,
        false,
        true,
        useDepthTest,
        0,
      )
    })

    const finalizeFreeLine = useCallback(() => {
      const linePoints = currentLineRef.current
      if (linePoints.length >= 2) {
        if (!selectedProcedure) {
          console.warn('No procedure selected, cannot add drawing element')
        } else {
          // RDP + uniform-decimation + coord quantization. TubeGeometry caps at 64
          // tubular segments (free.tsx) so persisting thousands of raw samples is
          // wasted payload. See utils/freeLineSimplification.ts.
          const simplifiedPoints = simplifyFreeLinePoints(linePoints)
          const newElement: DrawingElement = {
            id: generateDrawingElementId(),
            drawMode: DrawMode.FREE_LINE,
            points: simplifiedPoints,
            color: drawColor,
            size: drawWidth,
            visibleState: DrawingElementVisibleState.VISIBLE,
            procedureId: selectedProcedure.id,
            timestamp: Date.now(),
            camera: controls
              ? {
                  position: {
                    x: camera.position.x,
                    y: camera.position.y,
                    z: camera.position.z,
                  },
                  target: {
                    x: controls.target.x,
                    y: controls.target.y,
                    z: controls.target.z,
                  },
                }
              : undefined,
          }
          console.log('Adding new free line:', newElement)
          handleAddDrawingElement(newElement)
        }
      }

      isDrawingRef.current = false
      currentLineRef.current = []
      previewDirtyRef.current = true
      invalidate()
    }, [
      selectedProcedure,
      generateDrawingElementId,
      drawColor,
      drawWidth,
      controls,
      camera.position.x,
      camera.position.y,
      camera.position.z,
      handleAddDrawingElement,
      invalidate,
    ])

    const cancelFreeLine = useCallback(() => {
      isDrawingRef.current = false
      currentLineRef.current = []
      previewDirtyRef.current = true
      invalidate()
    }, [invalidate])

    const handleCanvasPointerDown = useCallback(
      (event: PointerEvent) => {
        beginPointerSession(pointerSessionRef.current, {
          pointerId: event.pointerId,
          clientX: event.clientX,
          clientY: event.clientY,
          isErasing,
        })

        if (isErasing) {
          capturePointerOnCanvas(event)
          return
        }

        if (!isDrawingLine || !scene) {
          return
        }

        const intersectionPoint = getSurfaceIntersection(event.clientX, event.clientY)
        if (!intersectionPoint) return

        event.stopPropagation()
        pointerSessionRef.current.consumed = true
        isDrawingRef.current = true
        currentLineRef.current = [intersectionPoint.clone()]
        previewDirtyRef.current = true
        capturePointerOnCanvas(event)
        invalidate()
      },
      [isErasing, isDrawingLine, scene, getSurfaceIntersection, capturePointerOnCanvas, invalidate],
    )

    // RAF-throttled: at most one raycast per animation frame.
    // Coordinates are stored in a ref; the RAF callback picks up the latest.
    const processDrawCoords = useCallback(() => {
      drawRafPendingRef.current = false
      const coords = pendingDrawCoordsRef.current
      if (!coords || !isDrawingRef.current) return
      pendingDrawCoordsRef.current = null

      const intersectionPoint = getSurfaceIntersection(coords.x, coords.y)
      if (intersectionPoint) {
        // Drop micro-movements: if the new sample sits closer than half a tube radius
        // to the previous one, it cannot be visually distinguished but doubles payload
        // size for slowly-drawn strokes. See utils/freeLineSimplification.ts.
        const linePts = currentLineRef.current
        if (linePts.length > 0) {
          const last = linePts[linePts.length - 1]
          const minSq = getMinCaptureDistanceSq(drawWidth)
          if (distanceSq(last, intersectionPoint) < minSq) {
            return
          }
        }
        linePts.push(intersectionPoint.clone())
        previewDirtyRef.current = true
        invalidate()
      }
    }, [getSurfaceIntersection, invalidate, drawWidth])

    const handleCanvasPointerMove = useCallback(
      (event: PointerEvent) => {
        if (isDrawingLine && isActivePointerSession(pointerSessionRef.current, event.pointerId)) {
          if (!isDrawingRef.current || !scene) return

          pendingDrawCoordsRef.current = { x: event.clientX, y: event.clientY }
          if (!drawRafPendingRef.current) {
            drawRafPendingRef.current = true
            requestAnimationFrame(processDrawCoords)
          }
        }

        if (
          (!isDrawingStraightLine && !isDrawingMeasure) ||
          drawLineState !== DrawLineState.START_POINT ||
          !scene
        )
          return

        const intersectionPoint = getSurfaceIntersection(event.clientX, event.clientY)
        if (intersectionPoint) {
          setPreviewEndPoint(intersectionPoint.clone())
          invalidate()
        }
      },
      [
        isDrawingLine,
        scene,
        processDrawCoords,
        isDrawingStraightLine,
        isDrawingMeasure,
        drawLineState,
        getSurfaceIntersection,
        invalidate,
      ],
    )

    const handleMarkerClick = useCallback(
      (event: PointerEvent) => {
        if ((!isAddingPin && !isAddingX) || !scene) return

        event.stopPropagation()

        const point = getSurfaceIntersection(event.clientX, event.clientY)

        if (!point) {
          if (debugMode) {
            console.log('No intersection found - raycasting failed')
            console.log('FBX position:', scene.position)
            console.log('FBX scale:', scene.scale)
            console.log('FBX bounding box:', new THREE.Box3().setFromObject(scene))
          }
          return
        }

        if (debugMode) console.log('Marker added at:', point)

        if (!selectedProcedure) {
          console.warn('No procedure selected, cannot add drawing element')
          return
        }

        if (isAddingX) {
          const newElement = {
            id: generateDrawingElementId(),
            drawMode: DrawMode.X,
            points: [{ x: point.x, y: point.y, z: point.z }],
            color: drawColor,
            size: drawWidth,
            procedureId: selectedProcedure.id,
            camera: controls
              ? {
                  position: { x: camera.position.x, y: camera.position.y, z: camera.position.z },
                  target: { x: controls.target.x, y: controls.target.y, z: controls.target.z },
                }
              : undefined,
          }
          handleAddDrawingElement(newElement)
        } else {
          const newElement = {
            id: generateDrawingElementId(),
            drawMode: DrawMode.PIN,
            points: [{ x: point.x, y: point.y, z: point.z }],
            color: drawColor,
            size: drawWidth,
            procedureId: selectedProcedure.id,
            camera: controls
              ? {
                  position: { x: camera.position.x, y: camera.position.y, z: camera.position.z },
                  target: { x: controls.target.x, y: controls.target.y, z: controls.target.z },
                }
              : undefined,
          }
          handleAddDrawingElement(newElement)
        }
      },
      [
        isAddingPin,
        isAddingX,
        scene,
        getSurfaceIntersection,
        drawColor,
        drawWidth,
        selectedProcedure,
        handleAddDrawingElement,
        debugMode,
        generateDrawingElementId,
        camera,
        controls,
      ],
    )

    const handleStraightLineClick = useCallback(
      (event: PointerEvent) => {
        if ((!isDrawingStraightLine && !isDrawingMeasure) || !scene) return

        event.stopPropagation()

        const intersectionPoint = getSurfaceIntersection(event.clientX, event.clientY)
        if (!intersectionPoint) return

        if (drawLineState === DrawLineState.IDLE) {
          setStraightLineStartPoint(intersectionPoint.clone())
          handleSetDrawLineState(DrawLineState.START_POINT)
        } else if (drawLineState === DrawLineState.START_POINT) {
          setStraightLineEndPoint(intersectionPoint.clone())

          if (straightLineStartPoint) {
            if (!selectedProcedure) {
              console.warn('No procedure selected, cannot add drawing element')
              setStraightLineStartPoint(null)
              setStraightLineEndPoint(null)
              setPreviewEndPoint(null)
              handleSetDrawLineState(DrawLineState.IDLE)
              return
            }

            const newElement: DrawingElement = {
              id: generateDrawingElementId(),
              drawMode: isDrawingMeasure ? DrawMode.MEASURE : DrawMode.STRAIGHT_LINE,
              points: [
                {
                  x: straightLineStartPoint.x,
                  y: straightLineStartPoint.y,
                  z: straightLineStartPoint.z,
                },
                {
                  x: intersectionPoint.x,
                  y: intersectionPoint.y,
                  z: intersectionPoint.z,
                },
              ],
              color: drawColor,
              size: normalizeStrokeWidthToFreeLinePreset(drawWidth),
              visibleState: DrawingElementVisibleState.VISIBLE,
              procedureId: selectedProcedure.id,
              timestamp: Date.now(),
              camera: controls
                ? {
                    position: {
                      x: camera.position.x,
                      y: camera.position.y,
                      z: camera.position.z,
                    },
                    target: {
                      x: controls.target.x,
                      y: controls.target.y,
                      z: controls.target.z,
                    },
                  }
                : undefined,
            }
            console.log(`Adding new ${isDrawingMeasure ? 'measure' : 'straight'} line:`, newElement)
            handleAddDrawingElement(newElement)

            if (isDrawingMeasure) {
              pendingMeasureLineRef.current = {
                id: newElement.id,
                tooltipPosition: {
                  x: intersectionPoint.x,
                  y: intersectionPoint.y,
                  z: intersectionPoint.z,
                },
              }
            }
          }

          setStraightLineStartPoint(null)
          setStraightLineEndPoint(null)
          setPreviewEndPoint(null)
          handleSetDrawLineState(DrawLineState.IDLE)
        }
      },
      [
        isDrawingStraightLine,
        isDrawingMeasure,
        drawLineState,
        straightLineStartPoint,
        scene,
        getSurfaceIntersection,
        handleSetDrawLineState,
        drawColor,
        drawWidth,
        selectedProcedure,
        handleAddDrawingElement,
        generateDrawingElementId,
      ],
    )

    const handleCanvasTap = useCallback(
      (event: PointerEvent) => {
        if (isAddingPin || isAddingX) {
          handleMarkerClick(event)
        } else if (isDrawingStraightLine || isDrawingMeasure) {
          handleStraightLineClick(event)
        }
      },
      [
        isAddingPin,
        isAddingX,
        isDrawingStraightLine,
        isDrawingMeasure,
        handleMarkerClick,
        handleStraightLineClick,
      ],
    )

    const handleEraseElementHit = useCallback(
      (element: DrawingElement, nativeEvent: PointerEvent) => {
        if (!isErasing || readOnly) {
          return false
        }

        if (!isElementErasable(element)) {
          return false
        }

        const identity = element.clientKey || element.id

        if (
          !isActivePointerSession(pointerSessionRef.current, nativeEvent.pointerId) ||
          !pointerSessionRef.current.isErasing
        ) {
          beginPointerSession(pointerSessionRef.current, {
            pointerId: nativeEvent.pointerId,
            clientX: nativeEvent.clientX,
            clientY: nativeEvent.clientY,
            isErasing: true,
            consumed: true,
          })
        }

        capturePointerOnCanvas(nativeEvent)

        if (!consumeEraseHit(pointerSessionRef.current, identity)) {
          return false
        }

        handleDeleteDrawingElement(identity)
        handleSetSelectedElement(null)

        return true
      },
      [
        isErasing,
        readOnly,
        capturePointerOnCanvas,
        handleDeleteDrawingElement,
        handleSetSelectedElement,
      ],
    )

    const handleElementPointerDown = useCallback(
      (event: ThreeEvent<PointerEvent>, element: DrawingElement) => {
        beginPointerSession(pointerSessionRef.current, {
          pointerId: event.nativeEvent.pointerId,
          clientX: event.nativeEvent.clientX,
          clientY: event.nativeEvent.clientY,
          isErasing,
          consumed: true,
        })

        if (isErasing) {
          event.stopPropagation()
          handleEraseElementHit(element, event.nativeEvent)
        }
      },
      [isErasing, handleEraseElementHit],
    )

    const handleElementPointerScrub = useCallback(
      (event: ThreeEvent<PointerEvent>, element: DrawingElement) => {
        if (
          !isErasing ||
          !isActivePointerSession(pointerSessionRef.current, event.nativeEvent.pointerId) ||
          !pointerSessionRef.current.isErasing
        ) {
          return
        }

        event.stopPropagation()
        handleEraseElementHit(element, event.nativeEvent)
      },
      [isErasing, handleEraseElementHit],
    )

    const handleElementClick = useCallback(
      (element: DrawingElement, clickPosition?: THREE.Vector3) => {
        if (isErasing) {
          return
        }

        suppressCanvasDeselectRef.current = true
        setTimeout(() => {
          suppressCanvasDeselectRef.current = false
        }, 0)
        const isSameElement = selectedElement?.element.id === element.id
        if (isSameElement) {
          handleSetSelectedElement(null)
        } else {
          const tooltipPosition = clickPosition
            ? { x: clickPosition.x, y: clickPosition.y, z: clickPosition.z }
            : undefined
          handleSetSelectedElement(element, tooltipPosition)
        }
      },
      [isErasing, selectedElement, handleSetSelectedElement],
    )

    const handleCanvasDeselect = useCallback(
      (event: PointerEvent) => {
        if (!(event.target instanceof HTMLCanvasElement)) return
        if (suppressCanvasDeselectRef.current) return
        handleSetSelectedElement(null)
      },
      [handleSetSelectedElement],
    )

    const handleCanvasPointerUp = useCallback(
      (event: PointerEvent) => {
        if (!isActivePointerSession(pointerSessionRef.current, event.pointerId)) {
          return
        }

        if (isDrawingLine) {
          finalizeFreeLine()
          resetActivePointerSession(event)
          return
        }

        if (!pointerSessionRef.current.isErasing) {
          const shouldHandleCanvasTap =
            !pointerSessionRef.current.consumed &&
            isTapGesture(pointerSessionRef.current, event.clientX, event.clientY)

          if (shouldHandleCanvasTap) {
            handleCanvasTap(event)
            handleCanvasDeselect(event)
          }
        }

        resetActivePointerSession(event)
      },
      [
        isDrawingLine,
        finalizeFreeLine,
        resetActivePointerSession,
        handleCanvasTap,
        handleCanvasDeselect,
      ],
    )

    const handleCanvasPointerCancel = useCallback(
      (event: PointerEvent) => {
        if (!isActivePointerSession(pointerSessionRef.current, event.pointerId)) {
          return
        }

        if (isDrawingLine) {
          cancelFreeLine()
        }

        resetActivePointerSession(event)
      },
      [isDrawingLine, cancelFreeLine, resetActivePointerSession],
    )

    // Stable per-element interaction callbacks — prevents new closure per render
    // which would defeat React.memo on Pin, StraightLine, FreeLine, ShapeElementRenderer.
    const elementInteractionHandlers = useMemo(() => {
      const map = new Map<
        string,
        {
          onClick: (event: { point?: THREE.Vector3; stopPropagation: () => void }) => void
          onLabelClick: (event: { stopPropagation: () => void }) => void
          onPointerDown: (event: ThreeEvent<PointerEvent>) => void
          onPointerEnter: (event: ThreeEvent<PointerEvent>) => void
          onPointerOver: (event: ThreeEvent<PointerEvent>) => void
        }
      >()
      for (const element of drawingElements) {
        map.set(element.clientKey || element.id, {
          onClick: (event) => {
            handleElementClick(element, event.point)
          },
          onLabelClick: () => {
            handleElementClick(element)
          },
          onPointerDown: (event) => {
            handleElementPointerDown(event, element)
          },
          onPointerEnter: (event) => {
            handleElementPointerScrub(event, element)
          },
          onPointerOver: (event) => {
            handleElementPointerScrub(event, element)
          },
        })
      }
      return map
    }, [
      drawingElements,
      selectedProcedure?.id,
      handleElementClick,
      handleElementPointerDown,
      handleElementPointerScrub,
    ])

    // Keep handlersRef in sync with latest callbacks every render.
    // This lets the mount-only effect below always call the current version.
    handlersRef.current = {
      pointerDown: handleCanvasPointerDown,
      pointerUp: handleCanvasPointerUp,
      pointerMove: handleCanvasPointerMove,
      pointerCancel: handleCanvasPointerCancel,
    }

    // Register event listeners ONCE on mount. Stable wrapper functions delegate
    // to handlersRef so the latest handlers are always called without needing to
    // tear down and re-add listeners on every mode/state change.
    React.useEffect(() => {
      const canvas = canvasRef.current
      if (!canvas) return

      const onPointerDown = (e: PointerEvent) => handlersRef.current.pointerDown(e)
      const onPointerUp = (e: PointerEvent) => handlersRef.current.pointerUp(e)
      const onPointerMove = (e: PointerEvent) => handlersRef.current.pointerMove(e)
      const onPointerCancel = (e: PointerEvent) => handlersRef.current.pointerCancel(e)

      canvas.addEventListener('pointerdown', onPointerDown)
      canvas.addEventListener('pointerup', onPointerUp)
      canvas.addEventListener('pointermove', onPointerMove)
      canvas.addEventListener('pointercancel', onPointerCancel)

      return () => {
        canvas.removeEventListener('pointerdown', onPointerDown)
        canvas.removeEventListener('pointerup', onPointerUp)
        canvas.removeEventListener('pointermove', onPointerMove)
        canvas.removeEventListener('pointercancel', onPointerCancel)
      }
    }, [])

    React.useEffect(() => {
      if (scene && (previousModelPathRef.current !== modelPath || !previousModelPathRef.current)) {
        previousModelPathRef.current = modelPath

        if (debugMode) console.log('GLB loaded, current scale:', scene.scale)

        scene.position.set(0, 0, 0)
        scene.scale.setScalar(60)
        if (debugMode) console.log('GLB scale set to:', scene.scale)

        const box = new THREE.Box3().setFromObject(scene)
        const center = box.getCenter(new THREE.Vector3())
        scene.position.sub(center)
        if (isV2ModelPath(modelPath)) {
          scene.position.x += V2_SCENE_OFFSET.x
          scene.position.y += V2_SCENE_OFFSET.y
          scene.position.z += V2_SCENE_OFFSET.z
        }
        if (debugMode) console.log('GLB centered at:', scene.position)

        const skinColorObj =
          skinColor && !isV2ModelPath(modelPath) ? new THREE.Color(skinColor) : null
        if (skinColorObj && debugMode) {
          console.log('Skin color applied:', skinColor)
        }

        scene.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.visible = true
            child.castShadow = true
            child.receiveShadow = true

            if (child.geometry) {
              child.geometry.computeBoundingBox()
              child.geometry.computeBoundingSphere()

              if (!child.geometry.attributes.position && debugMode) {
                console.warn('Mesh has no position attribute:', child.name)
              }

              child.updateMatrix()
              child.updateMatrixWorld(true)
            }

            if (child.material) {
              child.material.side = THREE.DoubleSide

              if (skinColorObj) {
                const applyMaterialEnhancements = (mat: THREE.MeshStandardMaterial) => {
                  mat.color = skinColorObj.clone()
                  mat.emissive = skinColorObj.clone().multiplyScalar(0.2)
                  mat.metalness = 0.1
                  mat.roughness = 0.7
                  mat.needsUpdate = true
                }

                if (Array.isArray(child.material)) {
                  child.material.forEach((mat) => {
                    if (mat instanceof THREE.MeshStandardMaterial) {
                      applyMaterialEnhancements(mat)
                    }
                  })
                } else if (child.material instanceof THREE.MeshStandardMaterial) {
                  applyMaterialEnhancements(child.material)
                }
              }
            }
          }
        })

        scene.updateMatrix()
        scene.updateMatrixWorld(true)
        if (debugMode) {
          console.log('GLB model loaded and prepared for raycasting')
          console.log('Final GLB scale:', scene.scale)
          console.log('Final GLB position:', scene.position)
        }

        setIsSceneReady(true)
        invalidate()
      }
    }, [scene, skinColor, debugMode, modelPath, invalidate])

    useImperativeHandle(
      ref,
      () => ({
        zoomToPin: (pinId: string) => {
          const pin = drawingElements.find((el) => el.id === pinId && el.drawMode === DrawMode.PIN)
          if (pin && controls) {
            const pinPosition = new Vector3(pin.points[0].x, pin.points[0].y, pin.points[0].z)

            const distance = 8
            const height = 3

            const cameraZ = pinPosition.z > 0 ? pinPosition.z + distance : pinPosition.z - distance

            const cameraPosition = new Vector3(pinPosition.x, pinPosition.y + height, cameraZ)

            const startPosition = camera.position.clone()
            const startTarget = controls.target.clone()
            const duration = 1000
            const startTime = performance.now()

            const animate = (progress: number) => {
              camera.position.lerpVectors(startPosition, cameraPosition, progress)
              controls.target.lerpVectors(startTarget, pinPosition, progress)
              controls.update()
            }

            const animationLoop = (currentTime: number) => {
              const elapsed = currentTime - startTime
              const progress = Math.min(elapsed / duration, 1)
              animate(progress)
              invalidate()

              if (progress < 1) {
                requestAnimationFrame(animationLoop)
              } else {
                persistSceneCoords()
              }
            }

            requestAnimationFrame(animationLoop)
          }
        },
        zoomToElement: (elementId: string) => {
          return new Promise<void>((resolve) => {
            const element = drawingElements.find((el) => el.id === elementId)
            if (!element || !controls) {
              resolve()
              return
            }

            const point = element.points[0]
            const target = new Vector3(point.x, point.y, point.z)

            const cameraDistance = 8
            const position = new Vector3(
              point.x,
              point.y + cameraDistance * 0.2,
              point.z + cameraDistance,
            )

            const startPosition = camera.position.clone()
            const startTarget = controls.target.clone()
            const duration = 1000
            const startTime = performance.now()

            const animate = (progress: number) => {
              camera.position.lerpVectors(startPosition, position, progress)
              controls.target.lerpVectors(startTarget, target, progress)
              controls.update()
            }

            const animationLoop = (currentTime: number) => {
              const elapsed = currentTime - startTime
              const progress = Math.min(elapsed / duration, 1)
              animate(progress)
              invalidate()

              if (progress < 1) {
                requestAnimationFrame(animationLoop)
              } else {
                persistSceneCoords()
                resolve()
              }
            }

            requestAnimationFrame(animationLoop)
          })
        },
        resetCamera: () => {
          if (controls) {
            camera.position.set(
              ProcedureInitialState.scene.position.x,
              ProcedureInitialState.scene.position.y,
              ProcedureInitialState.scene.position.z,
            )
            controls.target.set(
              ProcedureInitialState.scene.target.x,
              ProcedureInitialState.scene.target.y,
              ProcedureInitialState.scene.target.z,
            )
            controls.update()
            handleUpdateSceneCoords({
              position: {
                x: ProcedureInitialState.scene.position.x,
                y: ProcedureInitialState.scene.position.y,
                z: ProcedureInitialState.scene.position.z,
              },
              target: {
                x: ProcedureInitialState.scene.target.x,
                y: ProcedureInitialState.scene.target.y,
                z: ProcedureInitialState.scene.target.z,
              },
            })
            invalidate()
          }
        },
      }),
      [camera, controls, drawingElements, handleUpdateSceneCoords, invalidate, persistSceneCoords],
    )

    return (
      <>
        {}
        <primitive object={scene} ref={groupRef} />
        {}
        <ambientLight intensity={0.5} />
        {}
        <directionalLight position={[10, 12, 8]} intensity={0.75} />
        {}
        <directionalLight position={[-10, 12, 8]} intensity={0.75} />

        {renderableElements.map((element) => {
          const isSelected =
            selectedElement?.element.clientKey === element.clientKey ||
            selectedElement?.element.id === element.id
          const isFromSelectedProcedure = selectedProcedure?.id === element.procedureId
          const key = element.clientKey || `${element.id}-${element.timestamp.toString()}`
          const interactionHandlers = elementInteractionHandlers.get(
            element.clientKey || element.id,
          )
          if (element.drawMode === DrawMode.PIN) {
            return (
              <Pin
                key={key}
                element={element}
                isSelected={isSelected}
                isFromSelectedProcedure={isFromSelectedProcedure}
                useDepthTest={useDepthTest}
                onClick={isFromSelectedProcedure ? interactionHandlers?.onClick : undefined}
                onPointerDown={interactionHandlers?.onPointerDown}
                onPointerEnter={interactionHandlers?.onPointerEnter}
                onPointerOver={interactionHandlers?.onPointerOver}
                inactiveOpacity={inactiveProcedureOpacity}
              />
            )
          }

          if (
            element.drawMode === DrawMode.STRAIGHT_LINE ||
            element.drawMode === DrawMode.MEASURE
          ) {
            const points = element.points.map((point) => new Vector3(point.x, point.y, point.z))
            const cameraPos =
              element.camera?.position &&
              typeof element.camera.position.x === 'number' &&
              typeof element.camera.position.y === 'number' &&
              typeof element.camera.position.z === 'number'
                ? new Vector3(
                    element.camera.position.x,
                    element.camera.position.y,
                    element.camera.position.z,
                  )
                : undefined
            return (
              <StraightLine
                key={key}
                startPoint={points[0]}
                endPoint={points[1]}
                color={element.color}
                lineWidth={normalizeStrokeWidthToFreeLinePreset(element.size)}
                bodyMesh={isSceneReady ? scene : undefined}
                cameraPosition={cameraPos}
                showMeasurement={element.drawMode === DrawMode.MEASURE}
                showDirection={element.drawMode === DrawMode.STRAIGHT_LINE}
                isSelected={isSelected}
                isFromSelectedProcedure={isFromSelectedProcedure}
                description={element.description}
                useDepthTest={useDepthTest}
                onClick={isFromSelectedProcedure ? interactionHandlers?.onClick : undefined}
                onPointerDown={interactionHandlers?.onPointerDown}
                onPointerEnter={interactionHandlers?.onPointerEnter}
                onPointerOver={interactionHandlers?.onPointerOver}
                inactiveOpacity={inactiveProcedureOpacity}
              />
            )
          }

          if (element.drawMode === DrawMode.FREE_LINE) {
            const freeLineCamera =
              element.camera?.position &&
              typeof element.camera.position.x === 'number' &&
              typeof element.camera.position.y === 'number' &&
              typeof element.camera.position.z === 'number'
                ? new THREE.Vector3(
                    element.camera.position.x,
                    element.camera.position.y,
                    element.camera.position.z,
                  )
                : undefined
            return (
              <FreeLine
                key={key}
                points={element.points}
                color={element.color}
                lineWidth={element.size}
                bodyMesh={isSceneReady ? scene : undefined}
                cameraPosition={freeLineCamera ?? camera.position}
                isSelected={isSelected}
                isFromSelectedProcedure={isFromSelectedProcedure}
                description={element.description}
                descriptionPosition={element.descriptionPosition}
                useDepthTest={useDepthTest}
                onClick={isFromSelectedProcedure ? interactionHandlers?.onClick : undefined}
                onLabelClick={
                  isFromSelectedProcedure ? interactionHandlers?.onLabelClick : undefined
                }
                onPointerDown={interactionHandlers?.onPointerDown}
                onPointerEnter={interactionHandlers?.onPointerEnter}
                onPointerOver={interactionHandlers?.onPointerOver}
                inactiveOpacity={inactiveProcedureOpacity}
              />
            )
          }

          if (element.drawMode === DrawMode.X) {
            return (
              <XPin
                key={key}
                element={element}
                bodyMesh={isSceneReady ? scene : undefined}
                isSelected={isSelected}
                isFromSelectedProcedure={isFromSelectedProcedure}
                useDepthTest={useDepthTest}
                inactiveOpacity={inactiveProcedureOpacity}
                onClick={isFromSelectedProcedure ? interactionHandlers?.onClick : undefined}
                onPointerDown={interactionHandlers?.onPointerDown}
                onPointerEnter={interactionHandlers?.onPointerEnter}
                onPointerOver={interactionHandlers?.onPointerOver}
              />
            )
          }

          return null
        })}

        {/* Free-line preview: geometry updated imperatively by useFrame, no React re-renders */}
        <mesh ref={previewMeshRef} visible={false} />

        <LineDot
          position={straightLineStartPoint}
          color={previewColor}
          useDepthTest={useDepthTest}
        />

        {straightLineStartPoint && previewEndPoint && (
          <StraightLine
            startPoint={straightLineStartPoint}
            endPoint={previewEndPoint}
            color={previewColor}
            lineWidth={normalizeStrokeWidthToFreeLinePreset(drawWidth)}
            cameraPosition={camera.position}
            showMeasurement={isDrawingMeasure}
            showDirection={isDrawingStraightLine}
            isPreview={true}
            useDepthTest={useDepthTest}
          />
        )}

        {straightLineStartPoint && straightLineEndPoint && (
          <>
            <StraightLine
              startPoint={straightLineStartPoint}
              endPoint={straightLineEndPoint}
              color={drawColor}
              lineWidth={normalizeStrokeWidthToFreeLinePreset(drawWidth)}
              bodyMesh={isSceneReady ? scene : undefined}
              cameraPosition={camera.position}
              showMeasurement={isDrawingMeasure}
              showDirection={isDrawingStraightLine}
              useDepthTest={useDepthTest}
            />
            <LineDot
              position={straightLineEndPoint}
              color={drawColor}
              useDepthTest={useDepthTest}
            />
          </>
        )}

        <OrbitControls
          ref={setControls}
          enableDamping={false}
          enabled={orbitControlsEnabled}
          {...OrbitControlsSensitivity}
          minDistance={0.5}
          maxDistance={200}
          maxPolarAngle={Math.PI}
          target={cameraTarget}
          onChange={() => {
            if (controls && debugMode) {
              const position = camera.position
              const target = controls.target
              console.log('scene camera changed:', { position, target })
            }
          }}
          onEnd={() => persistSceneCoords()}
        />
      </>
    )
  },
)

export default Body
